import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Page,
  Layout,
  Card,
  EmptyState,
  IndexTable,
  Text,
  Badge,
  Tabs,
} from "@shopify/polaris";
import { usePurchaseOrders, type PurchaseOrder } from "../hooks/usePurchaseOrders";
import { formatCurrency } from "../utils/cost-calculations";

const STATUS_TONES: Record<string, "info" | "attention" | "success" | "critical" | undefined> = {
  submitted: "attention",
  partially_received: "attention",
  fully_received: "success",
};

const STATUS_LABELS: Record<string, string> = {
  submitted: "Awaiting Receiving",
  partially_received: "Partially Received",
  fully_received: "Fully Received",
};

const TABS = [
  { id: "receivable", content: "Ready to Receive", status: "submitted,partially_received" },
  { id: "fully_received", content: "Fully Received", status: "fully_received" },
];

export default function ReceivingList() {
  const navigate = useNavigate();
  const { purchaseOrders, loading, loadPurchaseOrders } = usePurchaseOrders();
  const [selectedTab, setSelectedTab] = useState(0);

  useEffect(() => {
    loadPurchaseOrders({ status: TABS[selectedTab].status });
  }, [selectedTab]);

  const handleTabChange = useCallback(
    (index: number) => setSelectedTab(index),
    []
  );

  const resourceName = {
    singular: "purchase order",
    plural: "purchase orders",
  };

  const emptyStateMarkup = selectedTab === 0 ? (
    <EmptyState
      heading="No purchase orders awaiting receiving"
      image="https://cdn.shopify.com/s/files/1/0262/4071/2726/files/emptystate-files.png"
    >
      <p>Submit a purchase order first, then come here to receive shipments.</p>
    </EmptyState>
  ) : (
    <EmptyState
      heading="No fully received purchase orders"
      image="https://cdn.shopify.com/s/files/1/0262/4071/2726/files/emptystate-files.png"
    >
      <p>Purchase orders will appear here once all items have been received.</p>
    </EmptyState>
  );

  const rowMarkup = purchaseOrders.map((po, index) => (
    <IndexTable.Row
      id={po.id}
      key={po.id}
      selected={false}
      position={index}
      onClick={() => {
        if (po.status === "submitted" || po.status === "partially_received") {
          navigate(`/purchase-orders/${po.id}/receive`);
        } else {
          navigate(`/purchase-orders/${po.id}`);
        }
      }}
    >
      <IndexTable.Cell>
        <Text variant="bodyMd" fontWeight="bold" as="span">
          {po.poNumber}
        </Text>
      </IndexTable.Cell>
      <IndexTable.Cell>{po.vendorName || "\u2014"}</IndexTable.Cell>
      <IndexTable.Cell>
        {po.orderDate ? new Date(po.orderDate).toLocaleDateString() : "\u2014"}
      </IndexTable.Cell>
      <IndexTable.Cell>
        <Badge tone={STATUS_TONES[po.status]}>
          {STATUS_LABELS[po.status] || po.status}
        </Badge>
      </IndexTable.Cell>
      <IndexTable.Cell>{formatCurrency(po.total)}</IndexTable.Cell>
      <IndexTable.Cell>{String(po.lineItemCount ?? 0)}</IndexTable.Cell>
    </IndexTable.Row>
  ));

  return (
    <Page title="Receiving">
      <Layout>
        <Layout.Section>
          <Card padding="0">
            <Tabs
              tabs={TABS.map((t) => ({ id: t.id, content: t.content, panelID: t.id }))}
              selected={selectedTab}
              onSelect={handleTabChange}
            >
              {purchaseOrders.length === 0 && !loading ? (
                <div style={{ padding: "16px" }}>{emptyStateMarkup}</div>
              ) : (
                <IndexTable
                  resourceName={resourceName}
                  itemCount={purchaseOrders.length}
                  selectedItemsCount={0}
                  onSelectionChange={() => {}}
                  headings={[
                    { title: "PO Number" },
                    { title: "Vendor" },
                    { title: "Date" },
                    { title: "Status" },
                    { title: "Total" },
                    { title: "Items" },
                  ]}
                  loading={loading}
                  selectable={false}
                >
                  {rowMarkup}
                </IndexTable>
              )}
            </Tabs>
          </Card>
        </Layout.Section>
      </Layout>
    </Page>
  );
}
